/**
 * What the cook row shows for one planned meal.
 *
 * A .ts for the same reason as approvalState.ts: tsconfig emits
 * `jsx: preserve`, so a .ts test can never import from a .tsx, and logic
 * inside a component is untestable here.
 *
 * Unlike approvals, this deliberately does NOT hide for non-recipe meals —
 * someone still handles the leftovers or books the restaurant (spec §4).
 */
import type { PlannedMeal } from '../../redux/mealPlanSlice';

export type CookState = 'hidden' | 'unassigned' | 'mine' | 'theirs';

export interface CookInput {
  meal: PlannedMeal | null | undefined;
  myUid: string | null;
  partnerUid: string | null;
  householdId: string | null;
  /** Today as YYYY-MM-DD, injected so the past-date rule stays testable. */
  today: string;
}

export const getCookState = ({
  meal, myUid, partnerUid, householdId, today,
}: CookInput): CookState => {
  if (!meal) return 'hidden';
  // Solo users see exactly today's card, with no nudge to pair.
  if (!householdId || !partnerUid) return 'hidden';
  // Nothing left to arrange once it has happened.
  if (meal.status === 'cooked') return 'hidden';
  if (meal.scheduledFor < today) return 'hidden';

  if (meal.cookId && meal.cookId === myUid) return 'mine';
  if (meal.cookId && meal.cookId === partnerUid) return 'theirs';
  // A cookId naming someone no longer in the household reads as unassigned.
  return 'unassigned';
};

/**
 * The avatar is a switch, not a picker: tap cycles empty -> me -> the other
 * -> empty (spec §5 principle 3). Returns the uid the next tap should write.
 */
export const nextCookOnTap = (
  current: string | undefined,
  myUid: string | null,
  partnerUid: string | null
): string | null => {
  if (!myUid) return null;
  if (!current) return myUid;
  if (current === myUid) return partnerUid ?? null;
  if (current === partnerUid) return null;
  // A cookId naming someone no longer in the household cycles to me.
  return myUid;
};
